// src/components/HomePage.jsx
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from './PageLoader';  // Changed to named import
import { createLogger } from '../utils/logger';
import { resolveLandingScope } from '../config/landing';

const logger = createLogger('Shell::HomePage');

const HomePage = () => {
    const navigate = useNavigate();
    const { loading, isSuperuser, hasAssignedCompany, primaryCompany, permissions } = useAuth();

    const isOperatorWithMachine =
        (permissions?.roles?.includes('operator') ?? false) && Boolean(permissions?.machines?.[0]);

    // A scoped user is sent to their own machine/factory by <OperatorRedirect> too.
    const hasScopedLanding = Boolean(
        resolveLandingScope({ permissions, isSuperuser, hasAssignedCompany })
    );

    useEffect(() => {
        if (loading) return;

        // Operators are routed to their assigned machine by <OperatorRedirect>, and so are
        // users whose asset scope names a single machine or factory. Bail out instead of
        // racing it: this is a mount effect, so StrictMode double-invokes it, and the
        // second run lands *after* OperatorRedirect's update effect and overwrites its
        // navigation — the user ends up on the company dashboard.
        if (isOperatorWithMachine || hasScopedLanding) {
            logger.info('Redirect owns this landing — deferring to OperatorRedirect', {
                reason: isOperatorWithMachine ? 'operator' : 'scoped'
            });
            return;
        }

        if (isSuperuser || !hasAssignedCompany) {
            logger.info('Redirecting to company selection', { isSuperuser });
            navigate('/app/dashboard', { replace: true });
        } else {
            logger.info('Redirecting to company dashboard', {
                companyId: primaryCompany.id,
                companyName: primaryCompany.name
            });
            navigate(`/app/dashboard/company/${primaryCompany.id}`, { replace: true });
        }
    }, [loading, isOperatorWithMachine, hasScopedLanding, isSuperuser, hasAssignedCompany,
        primaryCompany, navigate]);

    return <PageLoader />;
};

export default HomePage;